import {
  Button,
  Card,
  Dropdown,
  Image,
  Modal,
  notification,
  Pagination,
  Table,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useGetBreakpoints } from "../../hooks/useGetBreakpoints";
import { isArrayNonEmpty } from "../../util/ApiHelper";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircleFilled,
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InfoCircleFilled,
  MoreOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ListEmptyComponent from "../../components/ListEmptyComponent/ListEmptyComponent";
import { axiosInstance, BASE_API_CONFIG } from "../../util/Api";
import { formatDate } from "../../util/helper";
import { ItemDetailMobile } from "../../components/ItemDetailMobile";

import {
  deleteExporterOfferProductAction,
  getExporterOfferProductsAction,
  getProductsAction,
} from "../../appRedux/actions";
import EnquiryStatusTags from "../enquiries/EnquiriesStatus";
import { range } from "lodash";

const OfferProductsExporter = () => {
  const { isMobile } = useGetBreakpoints();
  const history = useHistory();
  const dispatch = useDispatch();
  const userData = useSelector(({ auth }) => auth?.userData);
  const userId = userData?.id;
  const { loading } = useSelector(({ common }) => common);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [userSubscription, setUserSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const [productLimits, setProductLimits] = useState({
    free: undefined,
    basic: undefined,
    premium: undefined,
  });

  const exporterOfferProductList = useSelector(
     (state) => state.exporterOfferProducts.exporterOfferProductsList || { data: [], meta: {} }
   );
   
    const rows = useMemo(() => {
      if (!exporterOfferProductList) return [];
      if (Array.isArray(exporterOfferProductList.data)) return exporterOfferProductList.data;
      if (Array.isArray(exporterOfferProductList)) return exporterOfferProductList;
      return [];
    }, [exporterOfferProductList]);

    const totalCount = useMemo(() => {
      return exporterOfferProductList?.meta?.total ?? exporterOfferProductList?.count ?? rows.length;
    }, [exporterOfferProductList, rows]);


  const productList = useSelector((state) => state.myProducts.productsList);

  const fetchSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const response = await axiosInstance.get(
        `${BASE_API_CONFIG.baseURL}/subscription-plans/user/${userId}`
      );
      setUserSubscription(response.data);
    } catch (err) {
      console.error("Subscription fetch error:", err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchSubscription();
      dispatch(getExporterOfferProductsAction(currentPage, pageSize));
      dispatch(getProductsAction());
    }
  }, [dispatch, userId, currentPage, pageSize]);

  const productLimit = useMemo(() => {
    if (!userSubscription || !productLimits) return productLimits?.FREE || 2;
    if (!userSubscription.isActive) return 2;
    switch (userSubscription.subscription?.planId) {
      case 1:
        return productLimits.FREE;
      case 2:
        return productLimits.BASIC;
      case 3:
        return productLimits.PREMIUM;
      default:
        return productLimits.FREE;
    }
  }, [userSubscription, productLimits]);

  const currentProductCount = useMemo(() => {
    if (Array.isArray(exporterOfferProductList)) {
      return exporterOfferProductList.length;
    }
    return (
      exporterOfferProductList?.rows?.length ||
      exporterOfferProductList?.count ||
      0
    );
  }, [exporterOfferProductList]);

  const canCreateMoreProducts = useMemo(() => {
    return currentProductCount < productLimit;
  }, [currentProductCount, productLimit]);

  const checkProductLimit = useCallback(() => {
    if (!userSubscription) {
      notification.warning({
        message: "Subscription Loading",
        description: "Please wait while we verify your subscription status",
      });
      return false;
    }
    if (currentProductCount >= productLimit) {
      Modal.confirm({
        title: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <ExclamationCircleFilled
              style={{ color: "#faad14", fontSize: 20 }}
            />
            <span>Product Limit Reached</span>
          </div>
        ),
        icon: null,
        width: 500,
        centered: true,
        content: (
          <div style={{ padding: "14px 0" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                backgroundColor: "#fffbe6",
                borderRadius: 8,
                marginBottom: 12,
              }}
            >
              <InfoCircleFilled style={{ color: "#faad14", fontSize: 18 }} />
              <div>
                <p style={{ margin: 0, fontWeight: 500 }}>
                  You've reached your free tier limit
                </p>
                <p style={{ margin: "4px 0 0 0", color: "#666" }}>
                  {currentProductCount}/{productLimit} freight rates used
                </p>
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 8 }}>Upgrade Benefits:</h4>
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 20,
                  listStyleType: "none",
                }}
              >
                <li
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <CheckCircleFilled
                    style={{ color: "#52c41a", marginRight: 8, marginTop: 4 }}
                  />
                  <span>Unlimited Products creation</span>
                </li>
                <li
                  style={{
                    marginBottom: 8,
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <CheckCircleFilled
                    style={{ color: "#52c41a", marginRight: 8, marginTop: 4 }}
                  />
                  <span>Priority listing in Products marketplace</span>
                </li>
                <li style={{ display: "flex", alignItems: "flex-start" }}>
                  <CheckCircleFilled
                    style={{ color: "#52c41a", marginRight: 8, marginTop: 4 }}
                  />
                  <span>Advanced analytics dashboard</span>
                </li>
              </ul>
            </div>
          </div>
        ),
        okText: (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <RocketOutlined />
            Upgrade Plan
          </span>
        ),
        cancelText: "Maybe Later",
        okButtonProps: {
          style: { background: "#1890ff", borderColor: "#1890ff" },
        },
        onOk: () => history.push("/subscription-User"),
      });
      return false;
    }
    return true;
  }, [userSubscription, currentProductCount, productLimit, history]);

  const navigateToDetails = useCallback(
    (id, isEditMode = false) => {
      const path = isEditMode
        ? `offer-your-cargo-exporter/add-edit/${id}`
        : `offer-your-cargo-exporter/add-edit`;
      history.push(path);
    },
    [history]
  );

  const navigateToViewDetails = useCallback(
    (id) => {
      const path = `offer-your-cargo-exporter/view/${id}`;
      history.push(path);
    },
    [history]
  );

  const handleCreateProduct = useCallback(() => {
    if (!checkProductLimit()) return;
    navigateToDetails();
  }, [checkProductLimit, navigateToDetails]);

  const handleAddProduct = () => {
    history.push("/my-products/edit");
  };

  const handleDelete = useCallback(
    (id) => {
      dispatch(deleteExporterOfferProductAction(id));
    },
    [dispatch]
  );

  const onShowSizeChange = useCallback(
    (current, pageSize) => {
      setCurrentPage(current);
      setPageSize(pageSize);
      dispatch(getExporterOfferProductsAction(current, pageSize));
    },
    [dispatch]
  );

  const handleClone = useCallback(
    (record) => {
      const { id, ...cloneData } = record;
      history.push({
        pathname: "offer-your-cargo-exporter/add-edit",
        state: { cloneData }
      });
    },
    [history]
  );

  const moreActionsRenderer = useCallback(
    (record) => (
      <div className="myexim-action-icon" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          menu={{
            items: [
              {
                label: "View",
                key: "1",
                icon: <EyeOutlined />,
                onClick: () => {
                  navigateToViewDetails(record?.id);
                },
              },
              {
                label: "Clone",
                key: "4",
                icon: <CopyOutlined />,
                onClick: () => {
                  handleClone(record);
                },
              },
              {
                label: "Edit",
                key: "2",
                icon: <EditOutlined />,
                onClick: () => {
                  navigateToDetails(record.id, true);
                },
              },
              {
                label: "Delete",
                key: "3",
                icon: <DeleteOutlined />,
                onClick: () => {
                  handleDelete(record.id);
                },
              },
            ],
          }}
          trigger={["click"]}
        >
          <MoreOutlined />
        </Dropdown>
      </div>
    ),
    [handleDelete, navigateToDetails, navigateToViewDetails, handleClone]
  );

  const columns = useMemo(
    () => [
      {
        title: "Updated Date",
        dataIndex: "updatedAt",
        width: "25%",
        render: (updatedAt) => <div>{formatDate(updatedAt)}</div>,
      },
      {
        title: "Product Name",
        dataIndex: "name",
        width: "25%",
        render: (text) => (
            <div className={"highlighted-column"}>{text}</div>
        ),
      },
      {
        title: "FOB/FOR Price",
        dataIndex: "price",
        render: (price, record) => (
          <div>{`${record.currency} ${price} / ${record.uom || ""}`}</div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        render: moreActionsRenderer,
      },
    ],
    [moreActionsRenderer]
  );

  const renderContent = useCallback((data, count) => {
    return isMobile ? (
      <>
      <Card bodyStyle={{ padding: 0 }}>
        {data.map((record) => {
          return (
            <div
              className="item-detail-mobile gx-flex-row gx-pointer gx-border-bottom gx-align-items-center"
              onClick={() => navigateToViewDetails(record.id)}
            >
              {/* <div className="item-image">
                <Image
                  className="gx-rounded-lg gx-size-60 gx-border-2"
                  preview={false}
                  alt={record.images?.[0].originalName || "No Image"}
                  src={record?.primaryImage}
                  fallback="data:image/png;base64,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"
                />
              </div> */}
              <div className="item-details gx-flex-1 gx-flex-column">
                <div className="item-posted-date">
                  Posted on {formatDate(record?.createdAt)}
                </div>
                <div className="item-primary-row highlighted-column gx-flex-row gx-justify-content-between">
                  <div className="item-primary-text">{record?.name}</div>
                </div>
                <div className="item-additional-content gx-flex-row gx-justify-content-between gx-align-items-center">
                  <div className="item-secondary-text">
                    <span style={{ color: "#555555", fontWeight: 600 }}>
                      FOB Price:{" "}
                    </span>
                    <span style={{ fontWeight: 500 }}>
                      {`${record.currency} ${record.price} / ${
                        record.uom || ""
                      }`}
                    </span>
                  </div>
                  <div>{moreActionsRenderer(record)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </Card>
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        total={count}
        onChange={onShowSizeChange}
        showSizeChanger
        style={{ marginTop: 16, textAlign: "right" }}
        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
      />
      </>
    ) : (
      <Table
        rowClassName={(_, index) =>
          index % 2 === 0 ? "table-row-light" : "table-row-dark"
        }
        onRow={(record) => {
          return {
            onClick: () => {
              navigateToViewDetails(record?.id);
            },
          };
        }}
        className="gx-table-responsive my-product-table myexim-table"
        total={count}
        columns={columns}
        dataSource={data}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          position: ["bottomRight"],
          showSizeChanger: true,
          total: count,
          onChange: onShowSizeChange,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        footer={() => {
          if (count > 0) {
            // const currentCount = data?.length || 0;
            // const shouldShowUpgrade =
            //   userSubscription.subscription?.planId !== 3;

            return (
              <div
                style={{
                  padding: "10px 0",
                  fontWeight: "bold",
                  borderTop: "1px solid #d9d9d9",
                }}
              >
                {/* {!canCreateMoreProducts ? (
                  <div style={{ color: "#ff4d4f" }}>
                    You've reached your product limit ({currentCount}/
                    {productLimit}).
                    {shouldShowUpgrade && (
                      <Button
                        type="link"
                        onClick={() => history.push("/subscription-User")}
                        style={{
                          padding: "0 4px",
                          fontWeight: 600,
                          border: "1px solid #1890ff",
                          borderRadius: "4px",
                          marginLeft: "8px",
                        }}
                      >
                        Upgrade Now
                      </Button>
                    )}
                  </div>
                ) : ( */}
                  <div>
                    {/* <div style={{ marginTop: 8 }}>
                      Products: {currentCount}/{productLimit}
                    </div> */}
                  </div>
                {/* )} */}
              </div>
            );
          }
          return null;
        }}
      />
    );
  }, [isMobile, navigateToViewDetails, moreActionsRenderer, columns, currentPage, pageSize, onShowSizeChange]);

  return (
    <Card
      bodyStyle={{ padding: "24px" }}
      style={{ width: "100%" }}
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="myexim-table-title">Offer Your Cargo</div>
          {isArrayNonEmpty(rows) && (
            <div style={{ paddingLeft: 15 }}>
              <Button type="primary" onClick={handleCreateProduct}>
                {isMobile ? "+" : "+ Create New"}
                {!canCreateMoreProducts &&
                  userSubscription?.subscription?.planId < 3}
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div>
        {isArrayNonEmpty(rows)
          ? renderContent(
              rows, totalCount
            )
          : !loading && (
              <ListEmptyComponent
                message={
                  <>
                    {productList?.rows?.length > 0 ? (
                          ""
                    ) : (
                      <>You don’t have any products. Please add products before creating a new offer.</>
                    )}
                  </>
                }
               buttonText={ productList?.rows?.length > 0 ? "+ Create New Offer" : "+ Create New Product"  }
                onButtonClick={
                  productList?.rows?.length > 0
                    ? handleCreateProduct
                    : handleAddProduct
                }
                buttonDisabled={!canCreateMoreProducts}
              />
            )}
      </div>
    </Card>
  );
};

export default OfferProductsExporter;
